/**
 * First-party, privacy-respecting visitor tracking.
 *
 * SINGLE SOURCE OF TRUTH for the anonymous visitor id, session id and the
 * attribution snapshot (landing page, referrer, UTM values, language, device).
 *
 * Rules:
 *  - No fingerprinting: no canvas/font/WebGL probing, no third-party pixels.
 *  - The visitor stays ANONYMOUS until they voluntarily submit a form.
 *  - Events are batched and sent fire-and-forget so the UI is never blocked.
 *  - Nothing here writes to the CRM — only src/lib/crmForms.ts does that.
 */
import { supabase } from '@/lib/supabase';

const VISITOR_KEY = 'gg_vid';
const SESSION_KEY = 'gg_sid';
const ATTR_KEY = 'gg_attr';
const SESSION_TTL_MS = 30 * 60 * 1000;

export type TrackEventName =
  | 'page_view'
  | 'cta_click'
  | 'plan_selected'
  | 'form_started'
  | 'form_completed'
  | 'registration_started'
  | 'registration_completed'
  | 'checkout_started'
  | 'checkout_completed'
  | 'language_changed'
  | 'search'
  | 'listing_viewed'
  // --- PWA install funnel (see src/lib/pwa.ts) ---
  | 'pwa_prompt_shown'
  | 'pwa_install_accepted'
  | 'pwa_install_dismissed'
  | 'pwa_installed';


export interface Attribution {
  landingPage?: string;
  referrer?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  firstSeen?: string;
  lastSeen?: string;
  source?: string;
}

const rid = () => {
  try {
    const a = new Uint8Array(12);
    crypto.getRandomValues(a);
    return Array.from(a).map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
  }
};

const safeGet = (k: string) => {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
};
const safeSet = (k: string, v: string) => {
  try {
    localStorage.setItem(k, v);
  } catch {
    /* storage blocked — tracking silently degrades */
  }
};

export const getVisitorId = (): string => {
  let id = safeGet(VISITOR_KEY);
  if (!id || !/^[a-zA-Z0-9_-]{8,64}$/.test(id)) {
    id = rid();
    safeSet(VISITOR_KEY, id);
  }
  return id;
};

export const getSessionId = (): string => {
  const raw = safeGet(SESSION_KEY);
  const now = Date.now();
  if (raw) {
    const [id, ts] = raw.split('.');
    if (id && Number(ts) && now - Number(ts) < SESSION_TTL_MS) {
      safeSet(SESSION_KEY, `${id}.${now}`);
      return id;
    }
  }
  const id = rid();
  safeSet(SESSION_KEY, `${id}.${now}`);
  return id;
};

export const deviceCategory = (): 'mobile' | 'tablet' | 'desktop' => {
  if (typeof window === 'undefined') return 'desktop';
  const w = window.innerWidth;
  if (w < 640) return 'mobile';
  if (w < 1024) return 'tablet';
  return 'desktop';
};

/** Captures landing page / referrer / UTM once, then keeps refreshing lastSeen. */
export const getAttribution = (): Attribution => {
  let stored: Attribution = {};
  try {
    stored = JSON.parse(safeGet(ATTR_KEY) || '{}');
  } catch {
    stored = {};
  }

  if (typeof window !== 'undefined') {
    const p = new URLSearchParams(window.location.search);
    const first = !stored.firstSeen;
    const next: Attribution = {
      landingPage: stored.landingPage || window.location.pathname + window.location.search,
      referrer: stored.referrer || document.referrer || '',
      utmSource: stored.utmSource || p.get('utm_source') || '',
      utmMedium: stored.utmMedium || p.get('utm_medium') || '',
      utmCampaign: stored.utmCampaign || p.get('utm_campaign') || '',
      utmTerm: stored.utmTerm || p.get('utm_term') || '',
      utmContent: stored.utmContent || p.get('utm_content') || '',
      firstSeen: stored.firstSeen || new Date().toISOString(),
      lastSeen: new Date().toISOString(),
    };
    next.source = next.utmSource || (next.referrer ? 'referral' : 'direct');
    if (first || stored.lastSeen !== next.lastSeen) safeSet(ATTR_KEY, JSON.stringify(next));
    return next;
  }
  return stored;
};

interface QueuedEvent {
  name: TrackEventName;
  path?: string;
  data?: Record<string, unknown>;
}

let queue: QueuedEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;

const flush = async () => {
  if (!queue.length) return;
  const events = queue;
  queue = [];
  timer = null;
  const attr = getAttribution();
  try {
    await supabase.functions.invoke('track-event', {
      body: {
        visitorId: getVisitorId(),
        sessionId: getSessionId(),
        language: document.documentElement.lang === 'es' ? 'es' : 'en',
        device: deviceCategory(),
        landingPage: attr.landingPage,
        referrer: attr.referrer,
        utm: {
          source: attr.utmSource,
          medium: attr.utmMedium,
          campaign: attr.utmCampaign,
          term: attr.utmTerm,
          content: attr.utmContent,
        },
        events,
      },
    });
  } catch {
    /* tracking must never break the app */
  }
};

/** Fire-and-forget event. Batched for ~1.2s so navigation stays instant. */
export const track = (name: TrackEventName, data?: Record<string, unknown>) => {
  try {
    queue.push({
      name,
      path: typeof window !== 'undefined' ? window.location.pathname : undefined,
      data,
    });
    if (queue.length >= 10) {
      void flush();
      return;
    }
    if (!timer) timer = setTimeout(() => void flush(), 1200);
  } catch {
    /* ignore */
  }
};

export const trackPageView = () => track('page_view');

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => void flush());
}
